import React, { ComponentProps, FC } from 'react';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    Spread,
} from 'lexical';
import { ElementFormatType, LexicalEditor } from 'lexical';
import {
    DecoratorBlockNode,
    SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';


export const InsertVideoComponent: FC<
    { url: string; } &
    Omit<
        ComponentProps<typeof BlockWithAlignableContents>,
        'children'
    >
> = ({
    url,
    className,
    format,
    nodeKey
}) => {
        return (
            <BlockWithAlignableContents
                className={className}
                format={format}
                nodeKey={nodeKey}
            >
                <video
                    src={url}
                    className="insert-video"
                    controls
                    style={{ width: "100%", maxWidth: "500px" }}
                ></video>
            </BlockWithAlignableContents>
        );
    }

export type SerializedInsertVideoNode = Spread<
    {
        url: string;
        type: 'insert-video';
        version: 1;
    },
    SerializedDecoratorBlockNode
>;

export class InsertVideoNode extends DecoratorBlockNode {
    __url: string;

    constructor(url: string, format?: ElementFormatType, key?: NodeKey) {
        super(format, key);
        this.__url = url;
    }

    static getType(): string {
        return 'insert-video'
    }

    static clone(node: InsertVideoNode): InsertVideoNode {
        return new InsertVideoNode(node.__url, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializedInsertVideoNode): InsertVideoNode {
        const node = $createInsertVideoNode(serializedNode.url);
        node.setFormat(serializedNode.format);
        return node;
    }

    exportJSON(): SerializedInsertVideoNode {
        return {
            ...super.exportJSON(),
            type: 'insert-video',
            version: 1,
            url: this.__url
        }
    }

    // DOM→Nodeに変換
    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-insert-video')) return null;

                return {
                    conversion: (
                        domNode: HTMLElement,
                    ): DOMConversionOutput | null => {
                        const url = domNode.getAttribute('data-video-url');
                        if (!url) return null;

                        const node = $createInsertVideoNode(url);
                        return { node };
                    },
                    priority: 1,
                };
            },
        };
    }

    // Node→DOMに変換（外部出力用）
    exportDOM(): DOMExportOutput {
        const element = document.createElement('div');
        element.setAttribute('data-insert-video', 'true');
        element.setAttribute('data-video-url', this.__url);
        return { element };
    }

    updateDOM(): false {
        return false
    }

    decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
        const embedBlockTheme = config.theme.embedBlock || {};
        const className = {
            base: embedBlockTheme.base || '',
            focus: embedBlockTheme.focus || ''
        };
        return (
            <InsertVideoComponent
                className={className}
                format={this.__format}
                nodeKey={this.getKey()}
                url={this.__url}
            />
        )
    }
};

export const $createInsertVideoNode = (url: string): InsertVideoNode => {
    return new InsertVideoNode(url);
}

export const $isInsertVideoNode = (
    node: InsertVideoNode | LexicalNode | null | undefined,
): node is InsertVideoNode => {
    return node instanceof InsertVideoNode;
}